import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AssistantTurn, useComposerTurnRemovalPreview } from "@/lib/api";

import { requestTitle } from "./utils";

interface RemoveRequestProps {
  turn: AssistantTurn | undefined;
  onCancel: () => void;
  onConfirm: () => void;
  isBusy: boolean;
}

/**
 * Confirm removing a request, naming what actually goes with it.
 *
 * A generated file that was never downloaded and never put forward for the
 * library exists in exactly one place, and removing the request is what deletes
 * it. So the confirmation asks the server what this particular request holds
 * rather than warning in general terms: a request that produced nothing says
 * so, and a file already queued for the library is reported as safe instead of
 * being mourned.
 */
export function RemoveRequest({ turn, onCancel, onConfirm, isBusy }: RemoveRequestProps) {
  const { data: removal, isLoading } = useComposerTurnRemovalPreview(turn?.turn_id);
  const lost = removal?.files_lost ?? [];
  const kept = removal?.files_kept ?? [];

  return (
    <Dialog open={turn !== undefined} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Remove this request?</DialogTitle>
          <DialogDescription>{turn ? requestTitle(turn) : ""}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-muted flex items-center gap-2 py-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking what this request holds…
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            {lost.length > 0 ? (
              <div className="border-outcome-escalated/30 bg-outcome-escalated-tint/50 space-y-2 rounded-lg border p-3">
                <p className="text-foreground flex items-start gap-2 font-medium">
                  <AlertTriangle className="text-outcome-escalated mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {lost.length === 1 ? "This file was" : "These files were"} generated here and
                    never added to the library. Removing the request deletes{" "}
                    {lost.length === 1 ? "it" : "them"}.
                  </span>
                </p>
                <ul className="text-foreground/80 ml-6 space-y-1 text-xs">
                  {lost.map((filename) => (
                    <li key={filename} className="font-mono break-all">
                      {filename}
                    </li>
                  ))}
                </ul>
                <p className="text-muted ml-6 text-xs leading-5">
                  Anything you already downloaded stays on your computer — this only removes the
                  copy the app is holding.
                </p>
              </div>
            ) : (
              <p className="text-muted">
                Nothing generated here is at risk — the request is removed from the list and that is
                all.
              </p>
            )}

            {kept.length > 0 && (
              <p className="text-muted text-xs leading-5">
                Safe:{" "}
                {kept.map((filename, index) => (
                  <span key={filename}>
                    {index > 0 && ", "}
                    <span className="font-mono">{filename}</span>
                  </span>
                ))}{" "}
                — already queued for the library, so {kept.length === 1 ? "it stays" : "they stay"}{" "}
                there.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isBusy || isLoading}>
            {isBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {lost.length > 0 ? "Remove and delete" : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
